import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  getTechPortfolio,
  TechPortfolioFilter,
  TechPortfolioResponse,
  TechPortfolioRow,
  TechPortfolioSort,
} from '@/lib/tech-api-client';

function formatPercent(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0 }).format(value);
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(0, 10);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function formatMembershipStatus(raw: string | null | undefined): { label: string; className: string } {
  const value = String(raw || '').trim().toLowerCase();
  if (value === 'active') return { label: 'Active', className: 'bg-green-100 text-green-800 border-green-200' };
  if (value === 'past_due') return { label: 'Payment Failed', className: 'bg-red-100 text-red-800 border-red-200' };
  if (value === 'suspended') return { label: 'Suspended', className: 'bg-orange-100 text-orange-800 border-orange-200' };
  if (value === 'pending_payment') {
    return { label: 'Pending Payment', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
  }
  if (value === 'cancelled') return { label: 'Cancelled', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  if (value === 'expired') return { label: 'Expired', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  return { label: raw || '—', className: 'bg-gray-100 text-gray-700 border-gray-200' };
}

function Chip({ label, className }: { label: string; className: string }) {
  return (
    <span className={['inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium', className].join(' ')}>
      {label}
    </span>
  );
}

function FilterButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-3 py-2 rounded-xl text-sm font-medium border transition-colors',
        active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

export default function TechPortfolioPage() {
  const { accessToken } = useTechAuth();

  const [filter, setFilter] = useState<TechPortfolioFilter>('all');
  const [sort, setSort] = useState<TechPortfolioSort>('newest');

  const [data, setData] = useState<TechPortfolioResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<TechPortfolioRow | null>(null);

  const load = async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);
    try {
      const res = await getTechPortfolio(accessToken, { filter, sort });
      setData(res);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, filter, sort]);

  const summary = data?.summary;
  const rows = useMemo(() => data?.rows || [], [data?.rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Portfolio</h1>
          <p className="text-gray-600 mt-1">Customers whose memberships were sold by you.</p>
        </div>

        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
          disabled={loading}
        >
          <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Active</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{summary ? summary.active : '—'}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">At risk</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{summary ? summary.at_risk : '—'}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Cancelled / expired</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{summary ? summary.cancelled : '—'}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Retention rate</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {summary ? formatPercent(summary.retention_rate) : '—'}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <FilterButton label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
            <FilterButton label="Active" active={filter === 'active'} onClick={() => setFilter('active')} />
            <FilterButton label="At Risk" active={filter === 'at_risk'} onClick={() => setFilter('at_risk')} />
            <FilterButton label="Cancelled" active={filter === 'cancelled'} onClick={() => setFilter('cancelled')} />
          </div>

          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-500">Sort</div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as TechPortfolioSort)}
              className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="renewal">Renewal Date</option>
              <option value="alpha">Alphabetical</option>
            </select>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Customer</th>
                <th className="py-2 pr-4">Plan</th>
                <th className="py-2 pr-4">Systems</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Start</th>
                <th className="py-2">Next renewal</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-gray-500">
                    No customers found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const status = formatMembershipStatus(row.status);

                  return (
                    <tr
                      key={row.membership_id}
                      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelected(row)}
                    >
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{row.customer_name || '—'}</div>
                        <div className="text-xs text-gray-500">{row.customer_address || '—'}</div>
                      </td>
                      <td className="py-3 pr-4">{row.plan_name || '—'}</td>
                      <td className="py-3 pr-4">{row.systems_count ?? '—'}</td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-2">
                          <Chip label={status.label} className={status.className} />
                          {row.at_risk && <Chip label="At risk" className="bg-red-100 text-red-800 border-red-200" />}
                        </div>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">{formatDateOnly(row.start_date)}</td>
                      <td className="py-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{formatDateOnly(row.next_renewal_date)}</div>
                        {row.days_until_renewal !== null && (
                          <div className="text-xs text-gray-500">in {row.days_until_renewal} days</div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.customer_name ? `Membership – ${selected.customer_name}` : 'Membership'}
        maxWidthClassName="max-w-2xl"
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500">Membership #</div>
                <div className="mt-1 font-medium text-gray-900">{selected.membership_number || selected.membership_id}</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500">Plan</div>
                <div className="mt-1 font-medium text-gray-900">{selected.plan_name || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500">Status</div>
                <div className="mt-2">
                  <Chip label={formatMembershipStatus(selected.status).label} className={formatMembershipStatus(selected.status).className} />
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500">Billing</div>
                <div className="mt-1 font-medium text-gray-900">{selected.billing_frequency || '—'}</div>
                <div className="text-xs text-gray-500 mt-1">next billing: {formatDateOnly(selected.next_billing_date)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500">Systems</div>
                <div className="mt-1 font-medium text-gray-900">{selected.systems_count ?? '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500">Tune-ups / year</div>
                <div className="mt-1 font-medium text-gray-900">{selected.tune_ups_per_year ?? '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-500">Visits used</div>
                <div className="mt-1 font-medium text-gray-900">
                  {selected.visits_used ?? '—'} / {selected.total_visits_allowed ?? '—'}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Dates</div>
              <div className="mt-2 text-sm text-gray-800">
                <div>Start: {formatDateOnly(selected.start_date)}</div>
                <div>Next renewal (spiff): {formatDateOnly(selected.next_renewal_date)}</div>
                {selected.cancelled_at && <div>Cancelled: {formatDateOnly(selected.cancelled_at)}</div>}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
